import React, { useState, useEffect } from 'react';
import { getStoredToken, clearStoredToken } from '../services/authSession';
import { Plus, Users, CheckCircle, Clock, AlertCircle, XCircle, ChevronRight, FileText, ArrowLeft, Trash2 } from 'lucide-react';
import OnboardingWizard from './OnboardingWizard';
import API from '../services/apiBase';

const formatApiError = (errorData, fallback = 'Request failed') => {
  if (!errorData) return fallback;

  // FastAPI validation errors: { detail: [{ loc, msg, ...}] }
  if (Array.isArray(errorData.detail)) {
    const msgs = errorData.detail
      .map((item) => item?.msg || item?.message || null)
      .filter(Boolean);
    if (msgs.length > 0) return msgs.join('; ');
  }

  if (typeof errorData.detail === 'string') return errorData.detail;
  if (typeof errorData.message === 'string') return errorData.message;

  if (errorData.detail && typeof errorData.detail === 'object') {
    return errorData.detail.msg || errorData.detail.message || fallback;
  }

  return fallback;
};

const ClientManagementPage = () => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [onboardingClientId, setOnboardingClientId] = useState(null); // Track which client is being onboarded
  const [reconfiguringClientId, setReconfiguringClientId] = useState(null);
  const [resyncingClientId, setResyncingClientId] = useState(null);
  
  // New client form
  const [newClient, setNewClient] = useState({
    name: '',
    display_name: '',
    contact_email: '',
    contact_phone: '',
    contact_person: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = getStoredToken();
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token) {
      window.location.href = '/login';
      return;
    }

    setCurrentUser(userData);

    try {
      // Chess staff only: super admins see every client, consultants their
      // assigned ones (the backend scopes /auth/my-clients accordingly).
      if (userData.role !== 'super_admin' && userData.role !== 'consultant') {
        setError('Access denied. This page is for Chess staff.');
        setLoading(false);
        return;
      }

      // Fetch clients
      const clientsResponse = await fetch(
        `${API}/auth/my-clients`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (clientsResponse.ok) {
        const clientsData = await clientsResponse.json();
        setClients(clientsData);

        // Users are managed by super admins only
        if (userData.role === 'super_admin') {
          const usersResponse = await fetch(
            `${API}/auth/users`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );

          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            setUsers(usersData);
          }
        }
      } else if (clientsResponse.status === 401) {
        // Token expired or invalid - redirect to login
        console.error('Authentication failed - token may be expired');
        clearStoredToken();
        localStorage.removeItem('user');
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      } else {
        const errorData = await clientsResponse.json().catch(() => ({}));
        setError(`Failed to load clients: ${errorData.detail || clientsResponse.statusText}`);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    const token = getStoredToken();

    try {
      const payload = {
        ...newClient,
        name: newClient.name?.trim(),
        display_name: newClient.display_name?.trim() || null,
        contact_email: newClient.contact_email?.trim() || null,
        contact_phone: newClient.contact_phone?.trim() || null,
        contact_person: newClient.contact_person?.trim() || null,
      };

      const response = await fetch(
        `${API}/clients`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const client = await response.json();
        setClients([...clients, client]);
        setShowCreateModal(false);
        setNewClient({
          name: '',
          display_name: '',
          contact_email: '',
          contact_phone: '',
          contact_person: ''
        });
        
        // Show success message
        alert(`Client "${client.name}" created successfully! You can now start the onboarding process.`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert('Error: ' + formatApiError(errorData, 'Failed to create client'));
      }
    } catch (err) {
      alert('Error creating client: ' + err.message);
    }
  };

  const startOnboarding = (clientId) => {
    // Show intake wizard inline for this client.
    // Also pin active_client_id so the global X-Client-Id header (attached by
    // src/index.js to every API call) matches the client we're onboarding.
    // Otherwise unscoped calls inside the wizard would silently target the
    // dashboard's previously selected client.
    localStorage.setItem('onboarding_client_id', clientId);
    localStorage.setItem('active_client_id', clientId);
    setOnboardingClientId(clientId);
  };

  const cancelOnboarding = () => {
    setOnboardingClientId(null);
    setReconfiguringClientId(null);
    localStorage.removeItem('onboarding_client_id');
    loadData();
  };

  const startReconfigure = (clientId) => {
    localStorage.setItem('onboarding_client_id', clientId);
    localStorage.setItem('active_client_id', clientId);
    setReconfiguringClientId(clientId);
    setOnboardingClientId(clientId);
  };

  const resyncRules = async (clientId) => {
    const token = getStoredToken();
    setResyncingClientId(clientId);
    try {
      // Get the intake for this client
      const listRes = await fetch(`${API}/intake/v2/list?client_id=${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!listRes.ok) throw new Error('Could not fetch intake list');
      const intakes = await listRes.json();
      const intake = intakes.find(i => i.client_id === clientId);
      if (!intake) throw new Error('No intake found for this client');

      const syncRes = await fetch(`${API}/intake/v2/${intake.id}/resync-rules`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await syncRes.json().catch(() => ({}));
      if (!syncRes.ok) throw new Error(data.detail || `HTTP ${syncRes.status}`);
      alert(`Rules re-synced from client's own uploaded files.\nGL rules: ${data.results?.gl_rules?.synced ? '✓' : '✗'}\nSchedule H: ${data.results?.schedule_h?.synced ? '✓' : '✗'}`);
    } catch (err) {
      alert('Re-sync failed: ' + err.message);
    } finally {
      setResyncingClientId(null);
    }
  };

  const activateClient = async (clientId) => {
    const token = getStoredToken();
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/login';
      return;
    }

    if (!window.confirm('Activate this client? This will make them fully active in the system.')) {
      return;
    }
    
    // Show loading state
    const activateBtn = document.querySelector(`button[data-activate="${clientId}"]`);
    const originalText = activateBtn?.innerHTML;
    if (activateBtn) {
      activateBtn.disabled = true;
      activateBtn.innerHTML = '<span class="animate-spin">⏳</span> Activating...';
    }
    
    try {
      const response = await fetch(`${API}/clients/${clientId}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Client-Id': clientId,
        }
      });
      
      if (response.ok) {
        const result = await response.json();

        // If GL/mappings are syncing in background, poll until done
        if (result.sync_status === 'processing') {
          if (activateBtn) {
            activateBtn.innerHTML = '<span class="animate-spin">⏳</span> Syncing GL data...';
          }
          const syncOk = await _pollSyncStatus(clientId, token, activateBtn);
          if (syncOk) {
            alert('Client activated and all data synced successfully!');
          } else {
            alert('Client activated but data sync had issues. Check the client data and retry if needed.');
          }
        } else {
          alert(result.message || 'Client activated successfully!');
        }
        loadData();
      } else {
        const err = await response.json();
        alert('Failed to activate: ' + (err.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to activate client: ' + err.message);
    } finally {
      if (activateBtn) {
        activateBtn.disabled = false;
        activateBtn.innerHTML = originalText;
      }
    }
  };

  /**
   * Poll GET /api/clients/{id}/sync-status until background GL+mapping sync
   * finishes.  Returns true if completed, false if errors or timeout.
   */
  const _pollSyncStatus = async (clientId, token, btn) => {
    const MAX_POLLS = 120;        // ~10 minutes at 5s intervals
    const INTERVAL_MS = 5000;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, INTERVAL_MS));
      try {
        const res = await fetch(`${API}/clients/${clientId}/sync-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const data = await res.json();
        const st = data.status;
        if (btn) {
          const pct = data.progress || 0;
          btn.innerHTML = `<span class="animate-spin">⏳</span> Syncing... ${pct}%`;
        }
        if (st === 'completed') return true;
        if (st === 'completed_with_errors' || st === 'failed') return false;
        // still running — keep polling
      } catch (_) { /* network blip, retry */ }
    }
    return false;  // timed out
  };

  const deleteClient = async (client) => {
    const token = getStoredToken();
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/login';
      return;
    }

    if (client.id === 'client_default') {
      alert('Default client cannot be deleted.');
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete client "${client.display_name || client.name}" and ALL tenant data? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API}/clients/${client.id}?permanent=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Client-Id': client.id,
        }
      });

      if (response.ok) {
        alert('Client permanently deleted.');
        await loadData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert('Failed to delete client: ' + formatApiError(errorData, 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to delete client: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'green', icon: CheckCircle, text: 'Active' },
      onboarding: { color: 'yellow', icon: Clock, text: 'Onboarding' },
      rules_pending: { color: 'blue', icon: Clock, text: 'Rules Pending' },
      suspended: { color: 'red', icon: XCircle, text: 'Suspended' },
      inactive: { color: 'gray', icon: AlertCircle, text: 'Inactive' }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded text-sm bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  const getUserCountForClient = (clientId) => {
    return users.filter(u => u.client_id === clientId).length;
  };
  
  // If currently onboarding a client, show the intake wizard instead
  if (onboardingClientId) {
    return <OnboardingWizard onCancel={cancelOnboarding} clientId={onboardingClientId} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg">Loading clients...</div>
      </div>
    );
  }

  if (error && error.includes('Access denied')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Client Management</h1>
              <p className="text-gray-600">Manage clients and onboarding processes</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
              {currentUser?.role === 'super_admin' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add New Client
              </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clients</p>
                <p className="text-3xl font-bold text-gray-900">{clients.length}</p>
              </div>
              <Users className="w-12 h-12 text-blue-600" />
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-3xl font-bold text-green-600">
                  {clients.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Onboarding</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {clients.filter(c => c.status === 'onboarding').length}
                </p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Clients List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">All Clients</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {clients.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No clients yet. Click "Add New Client" to get started.
              </div>
            ) : (
              clients.map((client) => (
                <div key={client.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {client.display_name || client.name}
                        </h3>
                        {getStatusBadge(client.status)}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Client ID:</span> {client.id}
                        </div>
                        <div>
                          <span className="font-medium">Contact:</span> {client.contact_email || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Users:</span> {getUserCountForClient(client.id)}
                        </div>
                      </div>

                      {client.status === 'onboarding' && client.onboarding_step && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500">
                            Onboarding Progress: <span className="font-medium">{client.onboarding_step}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {client.id !== 'client_default' && (
                        <button
                          onClick={() => deleteClient(client)}
                          className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50"
                          title="Delete client"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}

                      {client.status === 'onboarding' && !client.onboarding_completed && (
                        <button
                          onClick={() => startOnboarding(client.id)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          <FileText className="w-4 h-4" />
                          {client.onboarding_step ? 'Continue Onboarding' : 'Start Onboarding'}
                        </button>
                      )}

                      {client.status === 'rules_pending' && (
                        <button
                          data-activate={client.id}
                          onClick={() => activateClient(client.id)}
                          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Activate Client
                        </button>
                      )}

                      {client.status === 'active' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              localStorage.setItem('active_client_id', client.id);
                              localStorage.setItem('active_client_name', client.name);
                              window.location.href = '/client-config';
                            }}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                          >
                            View Rules & Config
                            <ChevronRight className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startReconfigure(client.id)}
                            className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                            title="Edit onboarding pool structure"
                          >
                            <FileText className="w-4 h-4" />
                            Re-configure
                          </button>
                          <button
                            onClick={() => resyncRules(client.id)}
                            disabled={resyncingClientId === client.id}
                            className="flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50"
                            title="Re-parse rules from this client's own uploaded files"
                          >
                            {resyncingClientId === client.id ? '⏳' : '🔄'} Re-sync Rules
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {client.created_at && (
                    <div className="text-xs text-gray-500 mt-2">
                      Created: {new Date(client.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Create New Client</h2>
            </div>

            <form onSubmit={handleCreateClient} className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newClient.name}
                    onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ABC Defense Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={newClient.display_name}
                    onChange={(e) => setNewClient({...newClient, display_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="ABC Defense"
                  />
                  <p className="text-xs text-gray-500 mt-1">Short name for display (optional)</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={newClient.contact_email}
                      onChange={(e) => setNewClient({...newClient, contact_email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="admin@abcdefense.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newClient.contact_phone}
                      onChange={(e) => setNewClient({...newClient, contact_phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={newClient.contact_person}
                    onChange={(e) => setNewClient({...newClient, contact_person: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-800">
                  After creating the client, you'll be able to start the onboarding process which includes:
                </p>
                <ul className="text-sm text-blue-800 mt-2 ml-4 list-disc space-y-1">
                  <li>Intake form (Chart of Accounts, Pool Definitions, Allocations)</li>
                  <li>AI-powered rule generation</li>
                  <li>Rule review and approval</li>
                  <li>Sample data testing</li>
                  <li>User account creation</li>
                  <li>Client activation</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagementPage;
